import { supabase } from '../../config/supabase';
import { Database } from '@travel-dashboard/shared';

export type Business = Database['travel']['Tables']['businesses']['Row'];

export interface CreateBusinessInput {
  name: string;
  tax_id?: string;
  logo?: string;
}

export interface UpdateBusinessInput {
  name?: string;
  tax_id?: string;
  logo?: string;
}

export const businessService = {
  /**
   * Get all businesses created by the current user
   */
  async getUserBusinesses(userId: string): Promise<Business[]> {
    const { data, error } = await supabase
      .from('businesses')
      .select('*')
      .eq('created_by', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching businesses:', error);
      throw error;
    }

    return data || [];
  },

  /**
   * Get a single business by ID
   */
  async getBusinessById(businessId: string): Promise<Business | null> {
    const { data, error } = await supabase
      .from('businesses')
      .select('*')
      .eq('id', businessId)
      .single();

    if (error) {
      console.error('Error fetching business:', error);
      throw error;
    }

    return data;
  },

  /**
   * Create a new business
   */
  async createBusiness(userId: string, input: CreateBusinessInput): Promise<Business> {
    const { data, error } = await supabase
      .from('businesses')
      .insert({
        ...input,
        created_by: userId,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating business:', error);
      throw error;
    }

    return data;
  },

  /**
   * Update an existing business
   */
  async updateBusiness(businessId: string, input: UpdateBusinessInput): Promise<Business> {
    const { data, error } = await supabase
      .from('businesses')
      .update(input)
      .eq('id', businessId)
      .select()
      .single();

    if (error) {
      console.error('Error updating business:', error);
      throw error;
    }

    return data;
  },

  /**
   * Delete a business
   */
  async deleteBusiness(businessId: string): Promise<void> {
    const { error } = await supabase
      .from('businesses')
      .delete()
      .eq('id', businessId);

    if (error) {
      console.error('Error deleting business:', error);
      throw error;
    }
  },

  /**
   * Assign a business to a user profile
   */
  async assignBusinessToUser(userId: string, businessId: string | null): Promise<void> {
    const { error } = await supabase
      .from('users')
      .update({ business_id: businessId })
      .eq('id', userId);

    if (error) {
      console.error('Error assigning business to user:', error);
      throw error;
    }
  },
};
